import { MapPin, Repeat, Users } from "lucide-react";
import { useMemo } from "react";

import { cn } from "@/lib/utils";
import type { CalendarAgendaLayout, CalendarShellSnapshot } from "../core/shell-types";
import {
  agendaSectionLabel,
  agendaTimeLabel,
  calendarById,
  dateKey,
  occurrenceColor,
  occurrenceIndex,
} from "./format";

export function AgendaView({
  snapshot,
  layout,
  compact,
  onSelectOccurrence,
}: {
  snapshot: CalendarShellSnapshot;
  layout: CalendarAgendaLayout;
  compact: boolean;
  onSelectOccurrence: (key: string) => void;
}) {
  const index = useMemo(() => occurrenceIndex(snapshot), [snapshot]);

  if (layout.sections.length === 0) {
    return (
      <div
        data-calendar-view="agenda"
        className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1 text-muted-foreground"
      >
        <p className="font-display text-lg">Nothing scheduled</p>
        <p className="text-sm">No events in this period.</p>
      </div>
    );
  }

  return (
    <section
      data-calendar-view="agenda"
      aria-label="Agenda"
      className="min-h-0 flex-1 overflow-y-auto"
    >
      {layout.sections.map((section) => (
        <section key={dateKey(section.date)} data-date={dateKey(section.date)}>
          <h3
            className={cn(
              "sticky top-0 z-10 flex items-center gap-2 border-b border-border/60 bg-background/95 px-4 py-1.5 text-xs font-medium uppercase tracking-wider backdrop-blur",
              section.isToday ? "text-primary" : "text-muted-foreground",
            )}
            aria-current={section.isToday ? "date" : undefined}
          >
            {section.isToday ? (
              <span className="rounded-full bg-primary px-1.5 py-px text-[10px] text-primary-foreground">
                Today
              </span>
            ) : null}
            {agendaSectionLabel(section.date, snapshot.today)}
          </h3>
          <ul aria-label={agendaSectionLabel(section.date, snapshot.today)}>
            {section.keys.map((key) => {
              const occurrence = index.get(key);
              if (!occurrence) {
                return null;
              }
              const color = occurrenceColor(snapshot, occurrence);
              const selected = snapshot.selectedOccurrenceKey === key;
              const calendar = calendarById(snapshot, occurrence.calendarId);
              return (
                <li key={key}>
                  <button
                    type="button"
                    data-occurrence-key={key}
                    aria-pressed={selected}
                    onClick={() => onSelectOccurrence(key)}
                    className={cn(
                      "flex w-full items-start gap-3 border-b border-border/40 px-4 text-left transition-colors hover:bg-accent/60",
                      compact ? "min-h-11 py-2.5" : "py-2",
                      selected && "bg-accent",
                      occurrence.status === "cancelled" && "opacity-60",
                    )}
                  >
                    <span
                      aria-hidden
                      className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ background: color }}
                    />
                    <span
                      className={cn(
                        "shrink-0 tabular-nums text-muted-foreground",
                        compact ? "w-24 text-xs" : "w-36 text-sm",
                      )}
                    >
                      {agendaTimeLabel(occurrence, section.date, snapshot.timeZone)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "block truncate text-sm",
                          occurrence.status === "cancelled" && "line-through",
                        )}
                      >
                        {occurrence.title || "(untitled)"}
                      </span>
                      {occurrence.locationSummary || calendar ? (
                        <span className="flex items-center gap-2 text-xs text-muted-foreground">
                          {occurrence.locationSummary ? (
                            <span className="flex min-w-0 items-center gap-1">
                              <MapPin aria-hidden className="h-3 w-3 shrink-0" />
                              <span className="truncate">{occurrence.locationSummary}</span>
                            </span>
                          ) : null}
                          {calendar && !compact ? (
                            <span className="truncate">{calendar.name}</span>
                          ) : null}
                        </span>
                      ) : null}
                    </span>
                    <span className="flex shrink-0 items-center gap-1 text-muted-foreground/70">
                      {occurrence.isRecurring ? (
                        <Repeat aria-label="Repeats" className="h-3.5 w-3.5" />
                      ) : null}
                      {occurrence.hasParticipants ? (
                        <Users aria-label="Has participants" className="h-3.5 w-3.5" />
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </section>
  );
}
